import { useContext, useEffect, useRef, useState } from 'react';
import usePatientsForDoctor from '../../../hooks/usePatientsForDoctor';
import useStudentsForOneTeacher from '../../../hooks/useStudentsForOneTeacher';
import AuthContext from '../../../contexts/AuthContext';
import { FirestoreDB, LogAnalyticsEvent } from '../../../utils/firebase/firebase';
import { addDoc, collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { ANALYTICS_KEYS } from '../../../constants/AnalyticKeys';
import { ACM } from '../../../constants/UserRoles';
import { useFullStudentGoals } from '../../../contexts/userDataContext';

// Helper functions
function pad(n) {
  return n < 10 ? '0' + n : n;
}

function formatTime(date) {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  return `${pad(formattedHours)}:${pad(minutes)} ${ampm}`;
}

const currentDate = new Date();
const formattedDateTime = formatTime(currentDate);

export default function useRemindersLogic({
  currentPatient,
  patient,
  isTeacherContext = false,
}) {
  const { user, typeOfUser } = useContext(AuthContext);
  const isDoctor = ACM[typeOfUser]?.isDoctor;
  const isTeacher = ACM[typeOfUser]?.isTeacher;
  const [patientData, setPatientData] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(
    patient?.id || currentPatient?.patientId || ''
  );
  const [selectedStudent, setSelectedStudent] = useState(
    isTeacher || isTeacherContext ? patient?.id || '' : ''
  );
  const [priority, setPriority] = useState('');
  const [value, setValue] = useState(new Date());
  const [time, setTime] = useState([formattedDateTime]);
  const [textReminder, setTextReminder] = useState('');
  const [showCreateReminder, setShowCreateReminder] = useState(true);
  const [selectedDates, setSelectedDates] = useState([]);
  const [active, setActive] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [allReminders, setAllReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successPopUp, setSuccessPopUp] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteType, setDeleteType] = useState('');
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [editingTargetId, setEditingTargetId] = useState('');

  // API hooks - Using optimized data fetching
  const { data: patients = [] } = usePatientsForDoctor(user?.uid);
  const { data: students = [] } = useStudentsForOneTeacher(user?.uid, typeOfUser);
  // Use basic student data for dropdowns (faster) instead of full student goals data
  const { data: studentsGoalData = [] } = useFullStudentGoals();

  const prevPatientIdRef = useRef(patient?.id);
  const prevDoctorPatientIdRef = useRef(patient?.id || currentPatient?.patientId);

  // Format date helper
  const formatDate = (date) => {
    const year = new Date(date).getFullYear();
    const month = String(new Date(date).getMonth() + 1).padStart(2, '0');
    const day = String(new Date(date).getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Utility functions
  const capitalizeWords = (name) => {
    return name
      ?.split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const truncateName = (name, limit = 10) => {
    const capitalized = capitalizeWords(name);
    if (capitalized?.length > limit) {
      return `${capitalized.slice(0, limit)}...`;
    }
    return capitalized;
  };

  // Event handlers
  const handleDateChange = (date) => {
    const formattedDate = formatDate(date);
    const isSelected = selectedDates.includes(formattedDate);

    if (isSelected) {
      const updatedDates = selectedDates.filter((d) => d !== formattedDate);
      setSelectedDates(updatedDates);
    } else {
      setSelectedDates([...selectedDates, formattedDate]);
    }
  };

  const handleTextChangeHandler = (e) => {
    setTextReminder(e.target.value);
  };

  const handlePriorityChange = (event) => {
    setPriority(event.target.value);
  };

  const addTimePicker = () => {
    setTime([...time, formattedDateTime]);
  };

  const deleteTimePicker = (indexToDelete) => {
    const updatedTimes = time.filter((_, index) => index !== indexToDelete);
    setTime(updatedTimes);
  };

  const updateTime = (index, newTime) => {
    try {
      const formattedNewTime = formatTime(new Date(newTime));
      const updatedTimes = [...time];
      updatedTimes[index] = formattedNewTime;
      setTime(updatedTimes);
    } catch (error) {
      console.error('Error updating time:', error);
    }
  };

  const deleteDatePicker = (indexToDelete) => {
    const updatedDates = selectedDates.filter(
      (_, index) => index !== indexToDelete
    );
    setSelectedDates(updatedDates);
  };

  const handleDeleteWithConfirmation = (type, index) => {
    setDeleteType(type);
    setDeleteIndex(index);
    setOpenDeleteDialog(true);
  };

  const resetForm = () => {
    const defaultPatientId = patient?.id || currentPatient?.patientId || '';
    
    if (isDoctor) {
      setSelectedPatient(defaultPatientId);
    } else if (isTeacher || isTeacherContext) {
      setSelectedStudent(defaultPatientId || selectedStudent);
    } else {
      setSelectedPatient('');
      setSelectedStudent('');
    }

    setValue(new Date());
    setTime([formattedDateTime]);
    setTextReminder('');
    setSelectedDates([]);
    setPriority('');
    setActive(true);
    setIsEditing(false);
    setEditingReminderId(null);
    setEditingTargetId('');
    setIsLoading(false);
  };

  const startEditReminder = (reminderInfo, entityId) => {
    if (!reminderInfo) return;

    const fallbackId =
      entityId ||
      reminderInfo?.patientId ||
      reminderInfo?.studentId ||
      selectedPatient ||
      selectedStudent ||
      patient?.id ||
      currentPatient?.patientId ||
      '';

    if (!fallbackId) return;

    if (isDoctor) {
      setSelectedPatient(fallbackId);
    } else {
      setSelectedStudent(fallbackId);
    }

    setTextReminder(reminderInfo?.message || '');
    setSelectedDates(
      Array.isArray(reminderInfo?.dates) ? reminderInfo.dates : []
    );
    setTime(
      Array.isArray(reminderInfo?.times) && reminderInfo.times.length > 0
        ? reminderInfo.times
        : [formattedDateTime]
    );
    setPriority(reminderInfo?.priority || '');
    setActive(
      typeof reminderInfo?.active === 'boolean' ? reminderInfo.active : true
    );
    setShowCreateReminder(true);
    setIsEditing(true);
    setEditingReminderId(reminderInfo?.id || null);
    setEditingTargetId(fallbackId);
  };

  const cancelEditReminder = () => {
    resetForm();
    setShowCreateReminder(false);
  };

  const handleViewAllReminders = () => {
    if (isEditing) {
      cancelEditReminder();
    } else {
      setShowCreateReminder(false);
    }
  };

  // API functions
  const setReminderHandler = async () => {
    setIsLoading(true);

    try {
      if (!user?.uid) {
        return;
      }

      const timeZone = Intl.DateTimeFormat().resolvedOptions()?.timeZone;
      const targetId = isDoctor
        ? selectedPatient || editingTargetId
        : selectedStudent || editingTargetId;

      if (!targetId) {
        console.error('No recipient selected for reminder');
        return;
      }

      const remindersData = {
        message: textReminder || '',
        dates: selectedDates || [],
        times: time || [],
        active: active,
        priority,
        timeZone,
        ...(ACM?.[typeOfUser]?.isDoctor
          ? { doctorId: user?.uid }
          : { teacherId: user?.uid }),
      };

      if (isEditing && editingReminderId) {
        await updateDoc(
          doc(
            FirestoreDB,
            'notifications',
            targetId,
            'reminders',
            editingReminderId
          ),
          remindersData
        );
      } else {
        await addDoc(
          collection(
            FirestoreDB,
            'notifications',
            targetId,
            'reminders'
          ),
          remindersData
        );
      }

      LogAnalyticsEvent(ANALYTICS_KEYS.DASHBOARD.REMINDERS, {
        message: textReminder || '',
        dates: selectedDates || [],
        times: time || [],
        active: active,
        priority,
        timeZone,
        ...(ACM?.[typeOfUser]?.isDoctor
          ? { doctorId: user?.uid }
          : { teacherId: user?.uid }),
      });

      setSuccessPopUp(true);
      await fetchReminders();

      if (isEditing) {
        cancelEditReminder();
      } else {
        resetForm();
      }
    } catch (error) {
      console.error('Error adding/updating reminder:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getRemindersData = async (patientId) => {
    setLoading(true);

    try {
      const remindersRef = collection(
        FirestoreDB,
        'notifications',
        patientId,
        'reminders'
      );
      const remindersSnapshot = await getDocs(remindersRef);
      const remindersData = remindersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return remindersData;
    } catch (error) {
      console.error('Error fetching reminders data:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReminders = async () => {
    if (!patientData && !studentsGoalData) return;

    const allReminders = [];

    if (ACM?.[typeOfUser]?.isDoctor) {
      for (const patient of patientData) {
        const reminderData = await getRemindersData(patient.id);
        allReminders.push({
          id: patient?.id,
          reminders: reminderData,
          name: patient?.fullname,
          photoURL: patient?.photoURL,
        });
      }
    } else if (isTeacherContext) {
      for (const teacher of patientData) {
        const reminderData = await getRemindersData(teacher.id);
        allReminders.push({
          id: teacher?.id,
          reminders: reminderData,
          name: teacher?.fullname || teacher?.name,
          photoURL: teacher?.photoURL,
        });
      }
    } else {
      for (const student of studentsGoalData) {
        const reminderData = await getRemindersData(student.id);
        allReminders.push({
          id: student?.id,
          reminders: reminderData,
          name: student?.fullname,
          photoURL: student?.photoURL,
        });
      }
    }
    setAllReminders(allReminders);
  };

  // Effects
  useEffect(() => {
    if ((isTeacher || isTeacherContext) && patient?.id && prevPatientIdRef.current !== patient.id) {
      setSelectedStudent(patient.id);
      prevPatientIdRef.current = patient.id;
    }
  }, [isTeacher, isTeacherContext, patient?.id]);

  useEffect(() => {
    const targetId = patient?.id || currentPatient?.patientId;
    if (isDoctor && targetId && prevDoctorPatientIdRef.current !== targetId) {
      setSelectedPatient(targetId);
      prevDoctorPatientIdRef.current = targetId;
    }
  }, [isDoctor, patient?.id, currentPatient?.patientId]);

  useEffect(() => {
    if (ACM[typeOfUser]?.getPatientData) {
      if (patients && ACM[typeOfUser]?.isDoctor) {
        const dataArray = patients?.map((each) => ({
          ...each?.patientInfo,
          id: each?.patientId,
        }));
        setPatientData(dataArray);
      } else if (students && ACM[typeOfUser]?.isTeacher) {
        const dataArray = students?.map((each) => ({
          ...each,
          id: each?.id,
        }));
        // When isTeacherContext is true, ensure the current teacher (patient) is included
        if (isTeacherContext && patient?.id) {
          const teacherExists = dataArray.some((t) => t.id === patient.id);
          if (!teacherExists) {
            dataArray.push({
              ...patient,
              id: patient.id,
            });
          }
        }
        setPatientData(dataArray);
      }
    }
  }, [selectedPatient, patients, students, typeOfUser, isTeacherContext, patient]);

  // Auto-select first alphabetically sorted item if no explicit selection exists
  useEffect(() => {
    // Only auto-select if there's no explicit selection from props
    const hasExplicitSelection = patient?.id || currentPatient?.patientId;
    
    if (ACM[typeOfUser]?.isDoctor && patientData.length > 0 && !hasExplicitSelection && !selectedPatient) {
      const sortedPatients = [...patientData].sort((a, b) =>
        (a?.fullname || a?.name || '')
          .toLowerCase()
          .localeCompare((b?.fullname || b?.name || '').toLowerCase())
      );
      if (sortedPatients[0]?.id) {
        setSelectedPatient(sortedPatients[0].id);
      }
    }
    
    if (ACM[typeOfUser]?.isTeacher && studentsGoalData.length > 0 && !selectedStudent && !isTeacherContext) {
      const sortedStudents = [...studentsGoalData].sort((a, b) =>
        (a?.fullname || a?.name || '')
          .toLowerCase()
          .localeCompare((b?.fullname || b?.name || '').toLowerCase())
      );
      if (sortedStudents[0]?.id) {
        setSelectedStudent(sortedStudents[0].id);
      }
    }
  }, [patientData, studentsGoalData, selectedPatient, selectedStudent, typeOfUser, patient, currentPatient]);

  useEffect(() => {
    fetchReminders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData, showCreateReminder]);

  return {
    // State
    patientData,
    selectedPatient,
    selectedStudent,
    priority,
    value,
    time,
    textReminder,
    showCreateReminder,
    selectedDates,
    active,
    isLoading,
    allReminders,
    loading,
    successPopUp,
    openDeleteDialog,
    deleteType,
    deleteIndex,
    studentsGoalData,
    isEditing,
    user,
    typeOfUser,

    // Setters
    setSelectedPatient,
    setSelectedStudent,
    setPriority,
    setValue,
    setTime,
    setTextReminder,
    setShowCreateReminder,
    setSelectedDates,
    setActive,
    setSuccessPopUp,
    setOpenDeleteDialog,
    setDeleteType,
    setDeleteIndex,

    // Handlers
    handleDateChange,
    handleTextChangeHandler,
    handlePriorityChange,
    handleViewAllReminders,
    addTimePicker,
    deleteTimePicker,
    updateTime,
    deleteDatePicker,
    handleDeleteWithConfirmation,
    startEditReminder,
    cancelEditReminder,
    setReminderHandler,
    fetchReminders,

    // Utilities
    capitalizeWords,
    truncateName,
    formatDate,
  };
} 